import { MobileDrawer } from '@components/MobileDrawer/MobileDrawer';
import { MultipleToggle } from '@components/MultipleToggle/MultipleToggle';
import { NotifPanelBody } from '@components/NotifBell/NotifPanelBody';
import { useNotifActivity } from '@components/NotifBell/useNotifActivity';
import { UploadPanelBody } from '@components/UploadStatus/UploadPanelBody';
import { useUploadActivity } from '@components/UploadStatus/useUploadActivity';
import { NotificationsNoneRounded } from '@mui/icons-material';
import { debugStore } from '@stores/Debug';
import { notificationStore } from '@stores/Notifications';
import { useUploadManager } from '@stores/UploadManager/UploadManager';
import { observer } from 'mobx-react';
import { useCallback, useEffect, useState } from 'react';
import { useLocation } from 'react-router';
import css from './MobileNotifCenter.module.scss';

type Tab = 'uploads' | 'notifications';

/**
 * Mobile unified notification center: a single bell FAB (bottom-right) that
 * opens a bottom drawer with an "Uploads" / "Notifications" segmented control.
 * Both tabs reuse the exact same panel bodies + controllers as the desktop
 * popovers (`UploadPanelBody`/`useUploadActivity`, `NotifPanelBody`/
 * `useNotifActivity`) — no duplicated UI or logic.
 *
 * Mounted only on mobile (see PageHeader), replacing the two desktop triggers.
 */
export const MobileNotifCenter = observer(() => {
  const [open, setOpen] = useState(false);
  const [tab, setTab] = useState<Tab>('notifications');
  const uploadManager = useUploadManager();
  const location = useLocation();

  // Uploads surface is still nominally behind `showUploadStatus` (currently
  // force-GA); gating the tab on it preserves the one-line re-gate escape hatch.
  const showUploads = debugStore.showUploadStatus;

  const closeDrawer = useCallback(() => setOpen(false), []);

  // Results / delete / details all open as drawers that STACK above this one
  // (Z_INDEX.aboveMobileDrawer = 1450), so none of them close it — the notif/
  // uploads drawer stays open underneath. Their modals are rendered at the
  // component root (persistent).
  const upload = useUploadActivity();
  const notif = useNotifActivity();

  // Close the drawer on navigation — a card's "Show in library" / a results
  // row's "View in library" changes the route, and the drawer would otherwise
  // stay open covering the destination page. Keyed on `location.key` (changes on
  // EVERY navigation) rather than `pathname`, because "View in library" for the
  // library you're already on only changes the `?mediaId=` query, not the path.
  useEffect(() => {
    setOpen(false);
  }, [location.key]);

  const openDrawer = useCallback(() => {
    // Default to Uploads only when something is actively uploading; otherwise
    // the bell metaphor → Notifications.
    setTab(showUploads && uploadManager.hasActiveUploads ? 'uploads' : 'notifications');
    setOpen(true);
  }, [showUploads, uploadManager]);

  // This component is the SOLE owner of the store's surface flags on mobile:
  // the desktop auto-open callers (UploadManager / pushRichEvent) are guarded to
  // desktop only via isMobileViewport(), so nothing else flips popoverOpen here.
  // While the drawer is open: upload polling runs (gated on popoverOpen) and
  // both surfaces' entries are marked read (combined badge clears; new entries
  // arriving while open are added read). While closed: both false, so new
  // uploads/events stay unread and the bell FAB badges them.
  useEffect(() => {
    notificationStore.setPopoverOpen(open && showUploads);
    notificationStore.setNotifBellPopoverOpen(open);
  }, [open, showUploads]);

  const uploadUnread = showUploads ? notificationStore.unreadCount : 0;
  const badgeCount = uploadUnread + notificationStore.notifBellUnreadCount;

  return (
    <>
      <button className={css.fab} aria-label="Notifications" onClick={openDrawer}>
        <NotificationsNoneRounded />
        {badgeCount > 0 && <span className={css.badge}>{badgeCount}</span>}
      </button>

      <MobileDrawer open={open} onClose={closeDrawer} onOpen={() => setOpen(true)} frosted>
        <div className={css.drawerInner}>
          {showUploads && (
            <MultipleToggle<Tab>
              className={css.toggle}
              value={tab}
              onChange={setTab}
              options={[
                { value: 'uploads', label: 'Uploads' },
                { value: 'notifications', label: 'Notifications' },
              ]}
            />
          )}
          <div className={css.body}>
            {showUploads && tab === 'uploads' ? (
              <UploadPanelBody handlers={upload.handlers} fullWidth />
            ) : (
              <NotifPanelBody handlers={notif.handlers} fullWidth />
            )}
          </div>
        </div>
      </MobileDrawer>

      {/* Persistent — rendered outside the drawer so they survive it closing. */}
      {upload.modals}
      {notif.modals}
    </>
  );
});
